import React from 'react';
import store from '../redux/store';
import { getConfiguredFields } from './ProjectUtils';
import { getProjectMetadata } from '../redux/project/sliceProjectMetadata';
import { getProjectAreaConfig } from '../redux/project/sliceProjectAreas';
import { getProjectDocTypes } from '../redux/project/sliceProjectDocTypes';
import { getDocCategories, getDocPackages, getDocStructureFields } from './DocUtils';
import { getProjectViewConfig } from '../redux/project/sliceProjectViews';
import { getProjectFileTypes } from '../redux/project/sliceProjectFileTypes';
import { getImportDocTypes } from '../redux/import/sliceImportDocTypes';
import { GridColDef, gridClasses } from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import ImportTableDocStatus from '../components/General/ImportTable/ImportTableDocStatus';
import {
    getImportDocuments,
    setDocuments,
    updateImportDocuments,
} from '../redux/import/sliceImportDocuments';
import { getImportViews } from '../redux/import/sliceImportViews';
import {
    IImportStructureFields,
    getImportFieldSchema,
    getImportFields,
    getImportStructure,
} from '../redux/import/sliceImportViewData';
import { getProjectDocStatus } from '../redux/project/sliceProjectDocStatus';
import { setSearchText, setExpandedNodes } from '../redux/import/sliceImportData';
import { setSelectedNodes } from '../redux/import/sliceDocnumberSidepanel';
import { ICategoryFields } from '../components/General/ImportTable/ImportTableCategoryCell';
import { DocumentAny, StructureViewMode } from '../types';
import { getProjectActionsConfig } from '../redux/project/sliceProjectActions';
import Icon from '@mdi/react';
import { mdiCursorMove } from '@mdi/js';
import ImportTableDragIndicator from '../components/General/ImportTable/ImportTableDragIndicator';
import {
    getAdditionalDocFields,
    getAdditionalDocFieldSchema,
} from '../redux/project/documents/sliceProjectAdditionalDocuments';

export const fetchProjectMetadata = (replicaId: string) => {
    store.dispatch(getProjectMetadata(replicaId));
};

export const fetchAreas = (replicaId: string) => {
    store.dispatch(getProjectAreaConfig(replicaId));
};

export const fetchProjectViews = (replicaId: string) => {
    store.dispatch(getProjectViewConfig(replicaId));
};

export const fetchProjectDocTypes = (replicaId: string) => {
    store.dispatch(getProjectDocTypes(replicaId));
};

export const fetchProjectDocStatus = (replicaId: string) => {
    store.dispatch(getProjectDocStatus(replicaId));
};

export const fetchImportDocTypes = (replicaId: string) => {
    store.dispatch(getImportDocTypes(replicaId));
};

export const fetchFileTypes = (replicaId: string) => {
    store.dispatch(getProjectFileTypes(replicaId));
};

export const fetchImportDocuments = (replicaId: string) => {
    const state = store.getState();
    const fields = state.importViewData.fields.data;
    const fieldSchema = state.importViewData.fieldSchema.data;

    store.dispatch(
        getImportDocuments({
            replicaId,
            fields: getConfiguredFields(fields, fieldSchema),
        }),
    );
};

export const fetchImportViewData = (replicaId: string) => {
    store.dispatch(getImportFields({ replicaId }));
    store.dispatch(getImportFieldSchema({ replicaId }));
    store.dispatch(getAdditionalDocFields(replicaId));
    store.dispatch(getAdditionalDocFieldSchema(replicaId));
    store.dispatch(getProjectActionsConfig(replicaId));
};

export const fetchImportViews = (replicaId: string) => {
    store.dispatch(getImportViews({ replicaId }));
};

export const fetchImportStructure = (replicaId: string, viewName: string) => {
    store.dispatch(
        getImportStructure({
            replicaId,
            viewName,
        }),
    );
};

export const resetImportArea = () => {
    store.dispatch(setSearchText(''));
    store.dispatch(setExpandedNodes([]));
    store.dispatch(setSelectedNodes({ current: '', all: [] }));
};

/** Set, add or remove the categories (or packages) of the specified documents. */
export const categorizeImportDocuments = (
    replicaId: string,
    documentIds: string[],
    categories: ICategoryFields[],
    action: 'append' | 'replace' | 'remove',
    mode: StructureViewMode,
    count?: number,
) => {
    const state = store.getState();
    const documents = state.importDocuments.data;

    const newDocuments = [];
    const updates = [];

    documents.forEach(document => {
        if (!documentIds.includes(document.UniversalID)) {
            newDocuments.push(document);
            return;
        }

        let newCategories = [];

        if (action === 'replace') {
            newCategories = categories;
        } else {
            const docCategories =
                mode === 'Category' ? getDocCategories(document) : getDocPackages(document);
            newCategories = docCategories
                .filter(x => categories.some(i => i.FullPath !== x.FullPath))
                .concat(action === 'append' ? categories : []);
        }

        const newSubjects = newCategories.map(x => x.FullPath);
        const newIds = newCategories.map(x => x.Id);

        newDocuments.push({
            ...document,
            ...{ [mode]: newSubjects, [`${mode}ID`]: newIds },
        });
        updates.push({
            UniversalID: document.UniversalID,
            ...{ [mode]: newSubjects, [`${mode}ID`]: newIds },
        });
    });

    if (updates.length) {
        fetchUpdateImportDocuments(replicaId, mode, JSON.stringify(updates), true, count);
    }

    store.dispatch(setDocuments(newDocuments));
};

export const fetchUpdateImportDocuments = async (
    replicaId: string,
    viewName: string,
    docsJson: string,
    isCategorization?: boolean,
    count?: number,
): Promise<void> => {
    const response = await store.dispatch(
        updateImportDocuments({
            replicaId,
            viewName,
            docsJson,
            isCategorization,
            count,
        }),
    );

    const updateResult = response.payload as object;

    const state = store.getState();
    const importDocuments = state.importDocuments.data;

    const newDocs = importDocuments.map(doc => {
        if (!Object.keys(updateResult).includes(doc.UniversalID)) {
            return doc;
        }

        return { ...doc, Validation: updateResult[doc.UniversalID] };
    });

    store.dispatch(setDocuments(newDocs));

    return Promise.resolve();
};

export const getAssignedDocsCount = (
    structureItem: IImportStructureFields,
    view?: StructureViewMode,
    documents?: DocumentAny[],
): number => {
    const state = store.getState();
    !documents && (documents = state.importDocuments.data);
    !view && (view = state.importData.currentView as StructureViewMode);

    const assignedDocs = documents.filter(doc =>
        getDocStructureFields(doc, view).some(c => c.Id === structureItem.IdDocIndex),
    );
    return assignedDocs.length;
};

export const createStatusColumn = (): GridColDef => {
    return {
        field: 'importStatus',
        headerName: 'Status',
        headerAlign: 'center',
        disableColumnMenu: true,
        disableReorder: true,
        filterable: false,
        resizable: false,
        sortable: false,
        align: 'center',
        width: 100,
        renderCell: ({ row }) => <ImportTableDocStatus row={row} />,
        renderHeader: () => <Box className={gridClasses.columnHeaderTitle}>Status</Box>,
    };
};

export const createDragColumn = (): GridColDef => {
    return {
        field: 'dragAndDrop',
        headerName: 'Drag & Drop',
        headerAlign: 'center',
        disableColumnMenu: true,
        disableReorder: true,
        filterable: false,
        resizable: false,
        sortable: false,
        align: 'center',
        width: 40,
        minWidth: 40,
        renderCell: ({ row, api }) => (
            <ImportTableDragIndicator visible={api.isRowSelected(row.NoteId)} />
        ),
        renderHeader: () => <Icon path={mdiCursorMove} size={1} />,
    };
};
